/** Inferred from renderer metadata, not a measurement of GPU acceleration. */
type RendererClass = 'hardware' | 'software' | 'unknown';

interface WebGLCapability {
  /**
   * `supported`: context creation succeeded and the context was not already lost.
   * `unavailable`: creation returned null, including policy or resource limits.
   * `error`: creation/state inspection threw, or the context was already lost.
   */
  status: 'supported' | 'unavailable' | 'error';
  /**
   * `hardware`: renderer contains a recognized GPU name.
   * `software`: renderer contains a known software-rendering marker.
   * `unknown`: metadata is hidden, unreadable, unrecognized, or no context exists.
   * Software markers take precedence. This does not describe Canvas 2D/uPlot.
   */
  rendererClass: RendererClass;
}

export interface BrowserGraphicsCapabilities {
  /**
   * Always `complete` after this probe finishes, even when individual probes fail.
   * RUM sets `browser.probeStatus` to `pending` before the deferred probe runs.
   */
  probeStatus: 'complete';
  /** WebGL 1: `supported`, `unavailable`, or `error`; see WebGLCapability.status. */
  webglStatus: WebGLCapability['status'];
  /** WebGL 1: inferred `hardware`, `software`, or `unknown`; not Canvas 2D acceleration. */
  webglRendererClass: RendererClass;
  /** WebGL 2: independently tested `supported`, `unavailable`, or `error`. */
  webgl2Status: WebGLCapability['status'];
  /** WebGL 2: inferred `hardware`, `software`, or `unknown`; not Canvas 2D acceleration. */
  webgl2RendererClass: RendererClass;
  /**
   * `true`: navigator.gpu.requestAdapter is a function; `false`: it is absent.
   * `null`: inspection threw. No adapter/device is requested, so `true` does
   * not establish usable WebGPU, GPU acceleration, or supported device features.
   */
  webgpuApiAvailable: boolean | null;
  /**
   * `true`: OffscreenCanvas is exposed as a function; `false`: it is absent.
   * `null`: inspection threw. Construction, context types, transfer to a worker,
   * and rendering inside a worker are not tested.
   */
  offscreenCanvasApiAvailable: boolean | null;
}

/** Runs once per RUM initialization; callers own deduplication. */
export function scheduleBrowserGraphicsCapabilities(
  report: (capabilities: BrowserGraphicsCapabilities) => void
): void {
  const collect = () => report(collectBrowserGraphicsCapabilities());
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(collect, {timeout: 5000});
  } else {
    window.setTimeout(collect, 1000);
  }
}

/** A snapshot of this page's capabilities, not a guarantee for future contexts. */
export function collectBrowserGraphicsCapabilities(): BrowserGraphicsCapabilities {
  const webgl = probeWebGL('webgl');
  const webgl2 = probeWebGL('webgl2');
  return {
    probeStatus: 'complete',
    webglStatus: webgl.status,
    webglRendererClass: webgl.rendererClass,
    webgl2Status: webgl2.status,
    webgl2RendererClass: webgl2.rendererClass,
    // API exposure does not establish adapter/device availability. Avoid waking
    // another GPU backend merely to enrich telemetry.
    webgpuApiAvailable: readApiAvailability(() => {
      const gpu = (
        navigator as Navigator & {
          gpu?: {requestAdapter?: unknown};
        }
      ).gpu;
      return typeof gpu?.requestAdapter === 'function';
    }),
    offscreenCanvasApiAvailable: readApiAvailability(
      () => typeof OffscreenCanvas === 'function'
    ),
  };
}

function readApiAvailability(read: () => boolean): boolean | null {
  try {
    return read();
  } catch {
    return null;
  }
}

function probeWebGL(contextType: 'webgl' | 'webgl2'): WebGLCapability {
  let gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  try {
    // Context modes cannot share a canvas. Keep the probe detached and small.
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    gl =
      contextType === 'webgl'
        ? canvas.getContext('webgl')
        : canvas.getContext('webgl2');
    if (gl == null) {
      return {status: 'unavailable', rendererClass: 'unknown'};
    }
    if (gl.isContextLost()) {
      return {status: 'error', rendererClass: 'unknown'};
    }
    return {status: 'supported', rendererClass: readRendererClass(gl)};
  } catch {
    return {status: 'error', rendererClass: 'unknown'};
  } finally {
    // Release the probe's contexts even if metadata inspection fails. Never
    // inspect or lose an application-owned canvas context.
    try {
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      // Cleanup is best effort; preserve the capability result.
    }
  }
}

function readRendererClass(gl: WebGLRenderingContext): RendererClass {
  try {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo == null) {
      return 'unknown';
    }
    const renderer: unknown = gl.getParameter(
      debugInfo.UNMASKED_RENDERER_WEBGL
    );
    if (typeof renderer !== 'string') {
      return 'unknown';
    }
    // Classification is a heuristic. Software markers take precedence over
    // vendor names in composite ANGLE strings. Do not send raw GPU identifiers.
    if (
      /swiftshader|llvmpipe|softpipe|software|basic render|osmesa/i.test(
        renderer
      )
    ) {
      return 'software';
    }
    if (/\b(apple|nvidia|radeon|intel|adreno|mali|powervr)\b/i.test(renderer)) {
      return 'hardware';
    }
    return 'unknown';
  } catch {
    // Privacy restrictions on renderer info do not mean WebGL is unavailable.
    return 'unknown';
  }
}
